import React, { useState, useEffect, useMemo } from 'react';
import {
    UserCheck,
    Clock,
    Calendar,
    CheckCircle,
    XCircle,
    AlertCircle,
    Search,
    Eye,
    DollarSign,
    TrendingUp,
    FileText,
    Minus
} from 'lucide-react';
import { collection, getDocs, getDoc, doc, setDoc, updateDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useToast } from '../../contexts/ToastContext';
import { Teacher } from '../../types';
import { AttendanceRecord, AttendanceSummary, AttendanceSettings } from '../../types/attendance';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Input from '../ui/Input';

interface TeacherAttendanceManagerProps {
    onBack: () => void;
}

const TeacherAttendanceManager: React.FC<TeacherAttendanceManagerProps> = ({ onBack }) => {
    const { showSuccess, showError } = useToast();

    // State
    const [teachers, setTeachers] = useState<Teacher[]>([]);
    const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
    const [attendanceSettings, setAttendanceSettings] = useState<AttendanceSettings | null>(null);
    const [loading, setLoading] = useState(true);

    // Filters and search
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(12); // December
    const [selectedYear, setSelectedYear] = useState(2025);

    // Modals
    const [showIndividualSummary, setShowIndividualSummary] = useState(false);
    const [showDailyReport, setShowDailyReport] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
    const [individualSummaryYear, setIndividualSummaryYear] = useState(2025);
    const [dailyReportMonth, setDailyReportMonth] = useState(12);
    const [dailyReportYear, setDailyReportYear] = useState(2025);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (teachers.length > 0) {
            fetchAttendanceRecords();
        }
    }, [selectedMonth, selectedYear, teachers]);

    // Auto-adjust month when year changes - if year is 2025, set month to December
    useEffect(() => {
        if (selectedYear === 2025 && selectedMonth < 12) {
            setSelectedMonth(12);
        }
    }, [selectedYear]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [teachersData, settingsData] = await Promise.all([
                fetchTeachers(),
                fetchAttendanceSettings()
            ]);
            setTeachers(teachersData);
            setAttendanceSettings(settingsData);
        } catch (error) {
            console.error('Error fetching data:', error);
            showError('Error', 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const fetchTeachers = async () => {
        try {
            const teachersRef = collection(db, 'teachers');
            const q = query(teachersRef, orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                dateOfBirth: doc.data().dateOfBirth?.toDate(),
                createdAt: doc.data().createdAt?.toDate(),
                updatedAt: doc.data().updatedAt?.toDate(),
                currentPosition: {
                    ...doc.data().currentPosition,
                    joiningDate: doc.data().currentPosition?.joiningDate?.toDate(),
                },
            })) as Teacher[];
        } catch (error) {
            console.error('Error fetching teachers:', error);
            return [];
        }
    };

    const fetchAttendanceSettings = async () => {
        try {
            const settingsRef = doc(db, 'attendance_settings', 'teacher');
            const settingsDoc = await getDoc(settingsRef);
            if (settingsDoc.exists()) {
                return {
                    id: settingsDoc.id,
                    ...settingsDoc.data(),
                    createdAt: settingsDoc.data().createdAt?.toDate(),
                    updatedAt: settingsDoc.data().updatedAt?.toDate(),
                } as AttendanceSettings;
            }
            // Return default settings if none exist
            return {
                id: 'teacher',
                employeeType: 'teacher',
                workingHoursPerDay: 6, // Teachers typically work 6 hours
                lateThresholdMinutes: 10,
                halfDayThresholdHours: 3,
                overtimeRate: 1.5,
                salaryPerDay: 1500, // Higher daily rate for teachers
                deductionPerAbsentDay: 1500,
                deductionPerLateDay: 300,
                createdAt: new Date(),
                updatedAt: new Date()
            } as AttendanceSettings;
        } catch (error) {
            console.error('Error fetching attendance settings:', error);
            return null;
        }
    };

    const getMonthName = (month: number) => {
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return months[month - 1];
    };

    const fetchAttendanceRecords = async () => {
        try {
            const records: AttendanceRecord[] = [];
            const year = selectedYear.toString();
            const monthName = getMonthName(selectedMonth);
            const yearMonth = `${year}_${monthName}`;

            // Fetch attendance for each teacher from the nested structure
            // Path: attendance/teacher/{teacherId}/{yearMonth}
            for (const teacher of teachers) {
                if (!teacher.teacherId) continue;

                try {
                    const monthRef = doc(db, 'attendance', 'teacher', teacher.teacherId, yearMonth);
                    const monthSnap = await getDoc(monthRef);

                    if (monthSnap.exists()) {
                        const monthData = monthSnap.data();

                        // Process each day in the month
                        Object.keys(monthData).forEach(dayStr => {
                            const dayData = monthData[dayStr];
                            if (dayData && typeof dayData === 'object') {
                                const day = parseInt(dayStr);
                                if (isNaN(day)) return; // Skip non-numeric keys

                                const date = new Date(selectedYear, selectedMonth - 1, day);

                                // Extract status and times
                                let status: 'present' | 'absent' | 'late' | 'half-day' | 'leave';
                                let checkIn: Date | null = null;
                                let checkOut: Date | null = null;

                                if (dayData.P) {
                                    status = 'present';
                                    if (dayData.checkIn_time) {
                                        const [hours, minutes] = dayData.checkIn_time.split(':');
                                        checkIn = new Date(date);
                                        checkIn.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                                    }
                                    if (dayData.checkout_time) {
                                        const [hours, minutes] = dayData.checkout_time.split(':');
                                        checkOut = new Date(date);
                                        checkOut.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                                    }
                                } else if (dayData.A) {
                                    status = 'absent';
                                } else if (dayData.L) {
                                    status = 'leave';
                                } else {
                                    return; // Skip invalid entries
                                }

                                records.push({
                                    id: `${teacher.id}_${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${dayStr.padStart(2, '0')}`,
                                    employeeId: teacher.id,
                                    employeeType: 'teacher',
                                    date: date,
                                    checkIn: checkIn,
                                    checkOut: checkOut,
                                    status: status,
                                    workingHours: 0,
                                    overtimeHours: 0,
                                    notes: dayData.reason || '',
                                    isHolidayWork: dayData.isHolidayWork === true,
                                    holidayWorkReason: dayData.holidayWorkReason || '',
                                    createdAt: new Date(),
                                    updatedAt: new Date()
                                });
                            }
                        });
                    }
                } catch (error) {
                    console.error(`Error fetching attendance for teacher ${teacher.teacherId}:`, error);
                    // Continue with other teachers even if one fails
                }
            }

            setAttendanceRecords(records);
        } catch (error) {
            console.error('Error fetching attendance records:', error);
            showError('Error', 'Failed to fetch attendance records');
        }
    };

    const filteredTeachers = useMemo(() => {
        return teachers.filter(teacher => {
            const matchesSearch =
                teacher.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                teacher.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                teacher.teacherId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                teacher.email.toLowerCase().includes(searchTerm.toLowerCase());

            return matchesSearch;
        });
    }, [teachers, searchTerm]);

    const getAttendanceForTeacher = (teacherId: string) => {
        return attendanceRecords.filter(record => record.employeeId === teacherId);
    };

    const getTeacherAttendanceSummary = (teacherId: string, month?: number, year?: number) => {
        const teacherRecords = attendanceRecords.filter(record => record.employeeId === teacherId);

        // Use provided month/year or default to selected month/year
        const targetMonth = month ?? selectedMonth;
        const targetYear = year ?? selectedYear;

        // Get all days in the selected month up to today (if current month) or all days (if past month)
        const today = new Date();
        const isCurrentMonth = targetMonth === today.getMonth() + 1 && targetYear === today.getFullYear();
        const totalDaysInMonth = new Date(targetYear, targetMonth, 0).getDate();
        const daysToCheck = isCurrentMonth ? today.getDate() : totalDaysInMonth;

        // Create a map of dates that have attendance records
        const recordDates = new Set<string>();
        teacherRecords.forEach(record => {
            const recordDate = record.date;
            if (recordDate.getMonth() === targetMonth - 1 && recordDate.getFullYear() === targetYear) {
                const dateKey = `${recordDate.getFullYear()}-${recordDate.getMonth()}-${recordDate.getDate()}`;
                recordDates.add(dateKey);
            }
        });

        // Count days with records by status
        const presentDays = teacherRecords.filter(record => {
            const recordDate = record.date;
            return record.status === 'present' &&
                recordDate.getMonth() === targetMonth - 1 &&
                recordDate.getFullYear() === targetYear;
        }).length;

        const leaveDays = teacherRecords.filter(record => {
            const recordDate = record.date;
            return record.status === 'leave' &&
                recordDate.getMonth() === targetMonth - 1 &&
                recordDate.getFullYear() === targetYear;
        }).length;

        const lateDays = teacherRecords.filter(record => {
            const recordDate = record.date;
            return record.status === 'late' &&
                recordDate.getMonth() === targetMonth - 1 &&
                recordDate.getFullYear() === targetYear;
        }).length;

        const halfDays = teacherRecords.filter(record => {
            const recordDate = record.date;
            return record.status === 'half-day' &&
                recordDate.getMonth() === targetMonth - 1 &&
                recordDate.getFullYear() === targetYear;
        }).length;

        // Count explicitly marked absent days
        const markedAbsentDays = teacherRecords.filter(record => {
            const recordDate = record.date;
            return record.status === 'absent' &&
                recordDate.getMonth() === targetMonth - 1 &&
                recordDate.getFullYear() === targetYear;
        }).length;

        // Calculate auto-absent days (days without any attendance record)
        let autoAbsentDays = 0;
        for (let day = 1; day <= daysToCheck; day++) {
            const checkDate = new Date(targetYear, targetMonth - 1, day);
            const dateKey = `${checkDate.getFullYear()}-${checkDate.getMonth()}-${checkDate.getDate()}`;

            // Skip weekends if needed (optional - you can remove this if weekends should count)
            // For now, we'll count all days

            // If no record exists for this date, count as absent
            if (!recordDates.has(dateKey)) {
                autoAbsentDays++;
            }
        }

        const totalAbsentDays = markedAbsentDays + autoAbsentDays;

        return {
            presentDays,
            absentDays: totalAbsentDays,
            leaveDays,
            lateDays,
            halfDays,
            totalDays: presentDays + totalAbsentDays + leaveDays + lateDays + halfDays
        };
    };

    const getTotalWorkingDays = () => {
        const today = new Date();
        const isCurrentMonth = selectedMonth === today.getMonth() + 1 && selectedYear === today.getFullYear();

        if (isCurrentMonth) {
            return today.getDate(); // Current day if current month
        } else {
            return new Date(selectedYear, selectedMonth, 0).getDate(); // Total days in month
        }
    };

    const calculateSalary = (teacherId: string, month: number, year: number, monthlySalary: number = 0) => {
        if (!attendanceSettings) return { deductions: 0, netSalary: 0 };

        const monthRecords = attendanceRecords.filter(record => {
            const recordDate = record.date;
            return record.employeeId === teacherId &&
                recordDate.getMonth() === month - 1 &&
                recordDate.getFullYear() === year;
        });

        // Get summary which includes auto-absent days
        const summary = getTeacherAttendanceSummary(teacherId, month, year);

        const lateDays = monthRecords.filter(record => {
            const recordDate = record.date;
            return record.status === 'late' &&
                recordDate.getMonth() === month - 1 &&
                recordDate.getFullYear() === year;
        }).length;

        // Use the absent days from summary (includes auto-absent)
        const absentDays = summary.absentDays;

        // Calculate deductions based on absent days and late days
        const deductions = (absentDays * attendanceSettings.deductionPerAbsentDay) +
            (lateDays * attendanceSettings.deductionPerLateDay);

        // Net salary = Monthly Salary - Deductions
        const netSalary = Math.max(0, monthlySalary - deductions);

        return { deductions, netSalary };
    };

    const handleIndividualSummary = (teacher: Teacher) => {
        setSelectedTeacher(teacher);
        setShowIndividualSummary(true);
    };

    const handleDailyReport = (teacher: Teacher) => {
        setSelectedTeacher(teacher);
        setShowDailyReport(true);
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'present':
                return <CheckCircle className="h-5 w-5 text-green-600" />;
            case 'absent':
                return <XCircle className="h-5 w-5 text-red-600" />;
            case 'late':
                return <AlertCircle className="h-5 w-5 text-yellow-600" />;
            case 'half-day':
                return <Clock className="h-5 w-5 text-blue-600" />;
            default:
                return <Clock className="h-5 w-5 text-gray-600" />;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'present':
                return 'bg-green-100 text-green-800';
            case 'absent':
                return 'bg-red-100 text-red-800';
            case 'late':
                return 'bg-yellow-100 text-yellow-800';
            case 'half-day':
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg">
                        <UserCheck className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Teacher Attendance</h2>
                        <p className="text-gray-600">Manage and monitor teacher attendance records</p>
                    </div>
                </div>
                <div className="flex items-center space-x-3">
                    <Button
                        variant="secondary"
                        onClick={onBack}
                    >
                        Back
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <Card glassy={true} className="islamic-pattern">
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="md:col-span-2">
                            <Input
                                label="Search Teachers"
                                placeholder="Search by name, ID, or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                icon={<Search className="h-4 w-4" />}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
                            <select
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            >
                                {Array.from({ length: 12 }, (_, i) => {
                                    const month = i + 1;
                                    const currentDate = new Date();
                                    const isCurrentYear = selectedYear === currentDate.getFullYear();
                                    const isFutureMonth = isCurrentYear && month > currentDate.getMonth() + 1;

                                    // If year is 2025, only show December (month 12)
                                    if (selectedYear === 2025 && month < 12) return null;

                                    // Don't show future months
                                    if (isFutureMonth) return null;

                                    return (
                                        <option key={month} value={month}>
                                            {new Date(0, i).toLocaleString('default', { month: 'long' })}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            >
                                {Array.from({ length: 10 }, (_, i) => {
                                    const year = 2025 + i; // Start from 2025
                                    const currentDate = new Date();
                                    const isFutureYear = year > currentDate.getFullYear();

                                    // Don't show future years
                                    if (isFutureYear) {
                                        return null;
                                    }

                                    return (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Teacher Attendance Summary */}
            <Card glassy={true} className="islamic-pattern overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Teacher Attendance Summary - {new Date(0, selectedMonth - 1).toLocaleString('default', { month: 'long' })} {selectedYear}
                    </h3>
                    <p className="text-sm text-gray-600">
                        Total Working Days: {getTotalWorkingDays()} | Showing {filteredTeachers.length} teachers
                    </p>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Teacher
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Present
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Absent
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Leave
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Late
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Monthly Salary
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Deductions
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Net Salary
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredTeachers.map((teacher) => {
                                const summary = getTeacherAttendanceSummary(teacher.id);
                                const monthlySalary = teacher.currentPosition?.salary || 0;
                                const salary = calculateSalary(teacher.id, selectedMonth, selectedYear, monthlySalary);

                                return (
                                    <tr key={teacher.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                                                    <UserCheck className="h-4 w-4 text-white" />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {teacher.firstName} {teacher.lastName}
                                                    </div>
                                                    <div className="text-sm text-gray-500">{teacher.teacherId}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                {summary.presentDays}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                {summary.absentDays}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {summary.leaveDays}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                {summary.lateDays}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-900">
                                            Rs. {(teacher.currentPosition?.salary || 0).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-red-600">
                                            Rs. {salary.deductions.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-emerald-600">
                                            Rs. {salary.netSalary.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                            <div className="flex items-center justify-center space-x-2">
                                                <button
                                                    onClick={() => handleIndividualSummary(teacher)}
                                                    className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg transition-colors"
                                                    title="Individual Summary"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDailyReport(teacher)}
                                                    className="p-1 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100 rounded-lg transition-colors"
                                                    title="Daily Report"
                                                >
                                                    <FileText className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* Individual Summary Modal */}
            {showIndividualSummary && selectedTeacher && (
                <Modal
                    isOpen={true}
                    onClose={() => setShowIndividualSummary(false)}
                    title={`${selectedTeacher.firstName} ${selectedTeacher.lastName} - Individual Summary`}
                    size="xl"
                >
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-900">Yearly Summary</h3>
                            <div className="flex items-center space-x-2">
                                <label className="text-sm font-medium text-gray-700">Year:</label>
                                <select
                                    value={individualSummaryYear}
                                    onChange={(e) => setIndividualSummaryYear(parseInt(e.target.value))}
                                    className="px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                >
                                    {Array.from({ length: 10 }, (_, i) => {
                                        const year = 2025 + i; // Start from 2025
                                        const currentDate = new Date();
                                        const isFutureYear = year > currentDate.getFullYear();
                                        if (isFutureYear) return null;
                                        return (
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {Array.from({ length: 12 }, (_, i) => {
                                const month = i + 1;
                                const currentDate = new Date();
                                const isFutureMonth = individualSummaryYear === currentDate.getFullYear() && month > currentDate.getMonth() + 1;

                                // If year is 2025, only show December (month 12)
                                if (individualSummaryYear === 2025 && month < 12) return null;

                                // Don't show future months
                                if (isFutureMonth) return null;

                                const monthlySalary = selectedTeacher.currentPosition?.salary || 0;
                                const monthSalary = calculateSalary(selectedTeacher.id, month, individualSummaryYear, monthlySalary);
                                const monthSummary = getTeacherAttendanceSummary(selectedTeacher.id, month, individualSummaryYear);

                                return (
                                    <div key={month} className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="font-medium text-gray-900 mb-2">
                                            {new Date(0, i).toLocaleString('default', { month: 'short' })}
                                        </h4>
                                        <div className="space-y-1 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Present:</span>
                                                <span className="font-medium text-green-600">{monthSummary.presentDays}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Absent:</span>
                                                <span className="font-medium text-red-600">{monthSummary.absentDays}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Leave:</span>
                                                <span className="font-medium text-blue-600">{monthSummary.leaveDays}</span>
                                            </div>
                                            <div className="flex justify-between border-t pt-1">
                                                <span className="text-gray-600">Salary:</span>
                                                <span className="font-bold text-emerald-600">Rs. {monthSalary.netSalary.toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Daily Report Modal */}
            {showDailyReport && selectedTeacher && (
                <Modal
                    isOpen={true}
                    onClose={() => setShowDailyReport(false)}
                    title={`${selectedTeacher.firstName} ${selectedTeacher.lastName} - Daily Report`}
                    size="xl"
                >
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
                                <select
                                    value={dailyReportMonth}
                                    onChange={(e) => setDailyReportMonth(parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                >
                                    {Array.from({ length: 12 }, (_, i) => {
                                        const month = i + 1;
                                        const currentDate = new Date();
                                        const isFutureMonth = dailyReportYear === currentDate.getFullYear() && month > currentDate.getMonth() + 1;

                                        // If year is 2025, only show December (month 12)
                                        if (dailyReportYear === 2025 && month < 12) return null;

                                        // Don't show future months
                                        if (isFutureMonth) return null;

                                        return (
                                            <option key={month} value={month}>
                                                {new Date(0, i).toLocaleString('default', { month: 'long' })}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                                <select
                                    value={dailyReportYear}
                                    onChange={(e) => setDailyReportYear(parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                >
                                    {Array.from({ length: 10 }, (_, i) => {
                                        const year = 2025 + i; // Start from 2025
                                        const currentDate = new Date();
                                        const isFutureYear = year > currentDate.getFullYear();
                                        if (isFutureYear) return null;
                                        return (
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Check-in
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Check-out
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Working Hours
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Notes
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {getAttendanceForTeacher(selectedTeacher.id)
                                        .filter(record => {
                                            const recordDate = record.date;
                                            return recordDate.getMonth() === dailyReportMonth - 1 &&
                                                recordDate.getFullYear() === dailyReportYear;
                                        })
                                        .sort((a, b) => b.date.getTime() - a.date.getTime())
                                        .map((record) => (
                                            <tr key={record.id}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {record.date.toLocaleDateString()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}>
                                                        {record.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {record.checkIn ? record.checkIn.toLocaleTimeString() : '-'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {record.checkOut ? record.checkOut.toLocaleTimeString() : '-'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {record.workingHours ? `${record.workingHours.toFixed(1)} hrs` : '-'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {record.notes || '-'}
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default TeacherAttendanceManager;
